/* Phase 2A-2B — Project competitor profiles panel.
 * Founder/beta-user friendly:
 *  - Lists existing competitor profiles with name + website + social URLs.
 *  - Inline editor: add/edit name, website_url, instagram, linkedin,
 *    youtube, facebook, geography, category, notes.
 *  - "Analyze competitor websites" button → batch-fetches every profile
 *    that has a website_url.
 *  - Each row shows source_label badge: website_verified / user_provided / ai_inference.
 *  - Status badge: analyzed / failed / added.
 *
 * Backend contract:
 *  - GET    /api/projects/{id}/competitor-profiles
 *  - PATCH  /api/projects/{id}/competitor-profiles  (bulk upsert)
 *  - POST   /api/projects/{id}/competitors/research (batch analyze)
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Globe, Search, Loader2, CheckCircle2, AlertCircle, Trash2, Download } from "lucide-react";
import { downloadExport } from "@/lib/exports";

const SOURCE_LABEL_COLOR = {
    website_verified: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    user_provided:    "border-zinc-700 text-zinc-300 bg-zinc-900/40",
    ai_inference:     "border-violet-500/30 text-violet-300 bg-violet-500/5",
};

function emptyForm() {
    return {
        name: "",
        website_url: "",
        instagram_url: "",
        linkedin_url: "",
        youtube_url: "",
        facebook_url: "",
        geography: "",
        category: "",
        user_notes: "",
    };
}

export default function CompetitorProfilesPanel({ projectId }) {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [showForm, setShowForm] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/projects/${projectId}/competitor-profiles`);
            setProfiles(data.profiles || []);
        } catch (e) {
            toast.error(e.userMessage || "Could not load competitors");
        } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const name = (form.name || "").trim();
        if (!name) {
            toast.error("Competitor name is required.");
            return;
        }
        try {
            await api.patch(`/projects/${projectId}/competitor-profiles`,
                              { profiles: [form] });
            toast.success("Competitor saved.");
            setForm(emptyForm());
            setShowForm(false);
            await load();
        } catch (e) {
            toast.error(e.userMessage || "Could not save competitor");
        }
    };

    const analyzeAll = async () => {
        const withUrls = profiles.filter(p => p.website_url);
        if (withUrls.length === 0) {
            toast.error("Add at least one competitor with a website URL first.");
            return;
        }
        setAnalyzing(true);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/competitors/research`,
                { force_refresh: false }
            );
            const s = data.summary || {};
            toast.success(
                `Analyzed ${s.analyzed || 0} · cached ${s.cached || 0} · failed ${s.failed || 0} · skipped ${s.skipped || 0}`
            );
            await load();
        } catch (e) {
            toast.error(e.userMessage || "Competitor research failed");
        } finally { setAnalyzing(false); }
    };

    // Sprint A — internal-only deterministic competitor suggester.
    // Reads existing intake + competitor_seeds + approved evidence;
    // inserts ai_suggested + unconfirmed rows. Operator must confirm.
    const [quickFinding, setQuickFinding] = useState(false);
    const quickFind = async () => {
        setQuickFinding(true);
        try {
            const { data } = await api.post(
                `/research-intel/projects/${projectId}/competitors/quick-find`,
                {}
            );
            const c = data?.counters || {};
            toast.success(
                `Quick-find: ${c.inserted || 0} new (${c.skipped_existing || 0} skipped existing)`
            );
            await load();
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Quick-find failed");
        } finally { setQuickFinding(false); }
    };

    return (
        <div className="card-base p-5" data-testid="competitor-profiles-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Competitor profiles</h3>
                    <span className="text-xs text-zinc-500">{profiles.length} added</span>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => downloadExport(
                            `/api/exports/${projectId}/competitor.pdf`,
                            `competitor-${projectId}.pdf`,
                            (err) => toast.error(err?.message || "PDF download failed"),
                        )}
                        data-testid="competitor-pdf-btn"
                        className="btn-secondary text-xs"
                        title="Download the Competitor Analysis PDF (no agent re-run)."
                    >
                        <Download className="w-3.5 h-3.5" /> PDF
                    </button>
                    {profiles.some(p => p.website_url) && (
                        <button
                            type="button"
                            onClick={analyzeAll}
                            disabled={analyzing}
                            data-testid="competitors-analyze-btn"
                            className="btn-secondary text-xs disabled:opacity-50"
                        >
                            {analyzing
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Search className="w-3.5 h-3.5" />}
                            Analyze websites
                        </button>
                    )}
                    {!showForm && (
                        <button
                            type="button"
                            onClick={quickFind}
                            disabled={quickFinding}
                            data-testid="competitors-quick-find-btn"
                            title="Internal-only competitor suggester (no LLM, no SERP). Inserts pending candidates for operator confirmation."
                            className="btn-secondary text-xs disabled:opacity-50"
                        >
                            {quickFinding
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Search className="w-3.5 h-3.5" />}
                            Quick-find
                        </button>
                    )}
                    {!showForm && (
                        <button
                            type="button"
                            onClick={() => setShowForm(true)}
                            data-testid="competitor-add-btn"
                            className="btn-primary text-xs"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add competitor
                        </button>
                    )}
                </div>
            </div>

            <p className="text-xs text-zinc-500 mb-4 leading-relaxed" data-testid="competitor-help-text">
                <span className="text-zinc-300">Competitors are optional.</span>{" "}
                Add them manually with name, website, Instagram / Facebook / LinkedIn /
                YouTube, geography, category, and any notes — Furmais analyzes the
                homepage and feeds verified facts into Brand DNA / Research /
                Competitor Analyst with clear source attribution. Social URLs are
                stored but NOT fetched in this phase — they are marked{" "}
                <em>user_provided</em>.<br />
                If you skip this, Furmais can auto-suggest competitors from your
                client's industry + geography only — those rows are explicitly
                labeled <em>ai_inference / suggestion</em> and need manual
                verification before you use them as a real benchmark. Furmais
                does NOT scrape social platforms and does NOT run paid web search.
            </p>

            {showForm && (
                <div className="border border-zinc-800 rounded-sm p-4 mb-4 space-y-3 bg-zinc-950/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FormField label="Competitor name *">
                            <input
                                type="text" value={form.name}
                                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Pixar"
                                data-testid="competitor-name-input"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <FormField label="Website URL">
                            <input
                                type="url" value={form.website_url}
                                onChange={(e) => setForm(f => ({ ...f, website_url: e.target.value }))}
                                placeholder="https://pixar.com"
                                data-testid="competitor-website-input"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <FormField label="Instagram URL">
                            <input
                                type="url" value={form.instagram_url}
                                onChange={(e) => setForm(f => ({ ...f, instagram_url: e.target.value }))}
                                placeholder="https://instagram.com/pixar"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <FormField label="LinkedIn URL">
                            <input
                                type="url" value={form.linkedin_url}
                                onChange={(e) => setForm(f => ({ ...f, linkedin_url: e.target.value }))}
                                placeholder="https://linkedin.com/company/pixar"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <FormField label="YouTube URL">
                            <input
                                type="url" value={form.youtube_url}
                                onChange={(e) => setForm(f => ({ ...f, youtube_url: e.target.value }))}
                                placeholder="https://youtube.com/pixar"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <FormField label="Facebook URL">
                            <input
                                type="url" value={form.facebook_url}
                                onChange={(e) => setForm(f => ({ ...f, facebook_url: e.target.value }))}
                                placeholder="https://facebook.com/pixar"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <FormField label="Geography">
                            <input
                                type="text" value={form.geography}
                                onChange={(e) => setForm(f => ({ ...f, geography: e.target.value }))}
                                placeholder="USA, India, Global, …"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <FormField label="Category">
                            <input
                                type="text" value={form.category}
                                onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                                placeholder="Animation studio, SaaS, …"
                                className="input-base text-sm"
                            />
                        </FormField>
                        <div className="sm:col-span-2">
                            <FormField label="Notes (optional)">
                                <textarea
                                    rows={2} value={form.user_notes}
                                    onChange={(e) => setForm(f => ({ ...f, user_notes: e.target.value }))}
                                    placeholder="Anything strategically useful — pricing, distinguishing claims, etc."
                                    className="input-base text-sm w-full"
                                />
                            </FormField>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button
                            type="button"
                            onClick={() => { setForm(emptyForm()); setShowForm(false); }}
                            className="btn-secondary text-xs"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={save}
                            data-testid="competitor-save-btn"
                            className="btn-primary text-xs"
                        >
                            Save competitor
                        </button>
                    </div>
                </div>
            )}

            {loading && <div className="text-xs text-zinc-500">Loading…</div>}
            {!loading && profiles.length === 0 && !showForm && (
                <div className="text-xs text-zinc-500 italic">
                    No competitors yet. Click <span className="text-zinc-300">Add competitor</span> above.
                </div>
            )}

            <div className="space-y-2">
                {profiles.map(p => <ProfileRow key={p.id} p={p} onChanged={load} projectId={projectId} />)}
            </div>
        </div>
    );
}

function ProfileRow({ p, onChanged, projectId }) {
    const label = p.source_label || "user_provided";
    const labelClass = SOURCE_LABEL_COLOR[label] || SOURCE_LABEL_COLOR.user_provided;
    const status = p.status || "added";

    return (
        <div
            className="border border-zinc-800 bg-zinc-950/50 rounded-sm px-3 py-2"
            data-testid={`competitor-profile-${p.id}`}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    {status === "analyzed" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                    {status === "failed"   && <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    <span className="text-sm text-zinc-200 truncate">{p.name}</span>
                    {p.website_url && (
                        <a
                            href={p.website_url} target="_blank" rel="noreferrer"
                            className="text-[10px] text-zinc-500 hover:text-zinc-300 truncate"
                            title={p.website_url}
                        >
                            ({new URL(p.website_url).host})
                        </a>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span
                        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${labelClass}`}
                        data-testid={`competitor-source-label-${p.id}`}
                    >
                        {label}
                    </span>
                    {status === "failed" && (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-red-500/30 text-red-300 bg-red-500/5">
                            failed
                        </span>
                    )}
                </div>
            </div>
            {(p.geography || p.category || p.user_notes) && (
                <div className="text-[11px] text-zinc-500 mt-1.5 space-y-0.5">
                    {p.geography && <div><span className="text-zinc-600">Geo:</span> {p.geography}</div>}
                    {p.category && <div><span className="text-zinc-600">Category:</span> {p.category}</div>}
                    {p.user_notes && <div className="italic text-zinc-400">{p.user_notes}</div>}
                </div>
            )}
        </div>
    );
}

function FormField({ label, children }) {
    return (
        <div>
            <div className="label-micro mb-1">{label}</div>
            {children}
        </div>
    );
}
